import React from 'react'
import ReactDOM from 'react-dom'
import { ExpertByCategoryQuery, ChatWidgetExpertFragment } from '#src/generated/graphql'
import { Transition, animated } from 'react-spring/renderprops.cjs'
import { UnregisterCallback } from 'history'
import { withRouter, RouteComponentProps } from 'react-router-dom'
import ContentfulQuery from '#src/containers/Contentful'
import ChatHeader from './ChatHeader'
import TypingIndicator from './TypingIndicator'
import MessageBubbleText from './MessageBubbleText'
import RichCard from './RichCard'
import ConversationStarter from '#src/components/ConversationStarter'
import CloseIcon from './close.svg'
import ChatIcon from './chat.svg'
import classnames from 'classnames'
import styles from './styles.css'

// The steps in the simulated chat
enum AnimationStep {
  Pending,
  Opened,
  MessageOneSent,
  MessageTwoSent,
  MessageThreeSent
}

// Order of steps, and delays before advancing
const steps: Array<{ id: AnimationStep; delay: number | null }> = [
  {
    id: AnimationStep.Pending,
    delay: 2000
  },
  {
    id: AnimationStep.Opened,
    delay: 2000
  },
  {
    id: AnimationStep.MessageOneSent,
    delay: 4000
  },
  {
    id: AnimationStep.MessageTwoSent,
    delay: 3000
  },
  {
    id: AnimationStep.MessageThreeSent,
    delay: null
  }
]

const LOCAL_STORAGE_STEP_KEY = 'chat-teaser-step'

interface ContentfulChatTeaserData {
  firstMessage: string
  richCardCallToAction: string
  richCardTitle: string
  secondMessage: string
}

type Diff<T, U> = T extends U ? never : T;
type NonNullable<T> = Diff<T, null | undefined>
export type CategoryWithPath = NonNullable<ExpertByCategoryQuery['category']['ancestorWithPath']>

export interface TeaserChatWidgetState {
  expanded: boolean
  step: typeof steps[0]
}

export interface TeaserChatWidgetProps extends RouteComponentProps<{}> {
  className?: string
  expert?: ChatWidgetExpertFragment
  pathCategory: CategoryWithPath
  getParent?: () => Element | null | undefined
  getAppRoot?: () => Element | null | undefined
}

class TeaserChatWidget extends React.Component<TeaserChatWidgetProps, TeaserChatWidgetState> {
  public unlisten?: UnregisterCallback

  public constructor(props: TeaserChatWidgetProps) {
    super(props)

    this.state = {
      expanded: false,
      step: steps[0]
    }

    this.expand = this.expand.bind(this)
    this.collapse = this.collapse.bind(this)
    this.getApplicationDomNode = this.getApplicationDomNode.bind(this)
    this.addApplicationClassNames = this.addApplicationClassNames.bind(this)
    this.removeApplicationClassNames = this.removeApplicationClassNames.bind(this)
    this.shrinkApplication = this.shrinkApplication.bind(this)
    this.expandApplication = this.expandApplication.bind(this)
    this.goToNextStep = this.goToNextStep.bind(this)
    this.queueGoToNextStep = this.queueGoToNextStep.bind(this)
    this.renderChatContents = this.renderChatContents.bind(this)
    this.renderExpandedChat = this.renderExpandedChat.bind(this)
    this.renderCollapsedChat = this.renderCollapsedChat.bind(this)
  }

  public componentDidMount() {
    this.addApplicationClassNames(styles.transform)

    // If the user navigates, collapse the widget
    if (this.props.history) {
      this.unlisten = this.props.history.listen(() => {
        if (window.matchMedia('(max-width: 40em)').matches) {
          this.collapse()
        }
      })
    }

    const persistedStepIndexAsString = window.localStorage.getItem(LOCAL_STORAGE_STEP_KEY)

    if (persistedStepIndexAsString) {
      this.setState({
        step: steps[parseInt(persistedStepIndexAsString, 10)]
      })
    }

    this.queueGoToNextStep()
  }

  public componentWillUnmount() {
    this.expandApplication()
    this.removeApplicationClassNames(styles.transform)

    if (this.unlisten) {
      this.unlisten()
    }
  }

  public getApplicationDomNode() {
    return this.props.getAppRoot && this.props.getAppRoot()
  }

  /**
   * Expand the widget
   */
  public expand() {
    // When the TeaserChatWidget expands, we shrink the rest of the application
    this.shrinkApplication()

    this.setState({
      expanded: true
    })
  }

  /**
   * Collapse the widget
   */
  public collapse() {
    // When the TeaserChatWidget collapses, we reset the rest of the application
    this.expandApplication()

    this.setState({
      expanded: false
    })
  }

  /**
   * Visually "detach" the rest of the application from the browser
   */
  public shrinkApplication() {
    const domNode = this.getApplicationDomNode()

    if (domNode) {
      this.addApplicationClassNames(styles.shrunk)

      if (domNode instanceof HTMLElement) {
        // Calculate the percentage we need to scale the element in order to shrink by 9px in each dimension
        const rect = domNode.getBoundingClientRect()
        const scaleY = 1 - 18 / rect.height
        const scaleX = 1 - 18 / rect.width

        // Add the transform
        domNode.style.transform = `scaleX(${scaleX}) scaleY(${scaleY})`
        domNode.style.webkitTransform = `scaleX(${scaleX} scaleY(${scaleY})`
      }
    }
  }

  /**
   * Re-attach the rest of the application from the browser
   */
  public expandApplication() {
    const domNode = this.getApplicationDomNode()

    if (domNode) {
      this.removeApplicationClassNames(styles.shrunk)

      if (domNode instanceof HTMLElement) {
        // Reset the scaling transform (TODO: Do we need to restore the previous value?)
        domNode.style.transform = ''
        domNode.style.webkitTransform = ''
      }
    }
  }

  /**
   * Utility function to add className(s) to the application element
   */
  public addApplicationClassNames(className: string) {
    const domNode = this.getApplicationDomNode()

    if (domNode) {
      domNode.classList.add(...className.split(' '))
    }
  }

  /**
   * Utility function to remove className(s) to the application element
   */
  public removeApplicationClassNames(className: string) {
    const domNode = this.getApplicationDomNode()

    if (domNode) {
      domNode.classList.remove(...className.split(' '))
    }
  }

  public goToNextStep() {
    const currentStepIndex = steps.findIndex(step => step.id === this.state.step.id)

    // If there is a next step, advance and queue the next step.
    if (currentStepIndex !== -1 && currentStepIndex < steps.length - 1) {
      // If we're advancing from the "Pending" state, expand the ChatHead
      if (this.state.step.id === AnimationStep.Pending) {
        this.setState({
          expanded: true
        })
      }

      this.setState({
        step: steps[currentStepIndex + 1]
      })

      this.queueGoToNextStep()

      window.localStorage.setItem(LOCAL_STORAGE_STEP_KEY, (currentStepIndex + 1).toString())
    }
  }

  public queueGoToNextStep() {
    if (this.state.step.delay) {
      setTimeout(this.goToNextStep, this.state.step.delay)
    }
  }

  public renderChatContents(content: ContentfulChatTeaserData) {
    const currentStep = this.state.step.id
    const expertName = this.props.expert && this.props.expert.firstName

    const messages: React.ReactNode[] = []
    let isTyping = false

    if (currentStep >= AnimationStep.MessageOneSent) {
      messages.push(
        <MessageBubbleText>{content.firstMessage}</MessageBubbleText>)
    }
    if (currentStep >= AnimationStep.MessageTwoSent) {
      messages.push(
        <MessageBubbleText>{content.secondMessage}</MessageBubbleText>)
    }
    if (currentStep >= AnimationStep.MessageThreeSent) {
      messages.push(
        <RichCard title={content.richCardTitle} cta={content.richCardCallToAction} pathCategory={this.props.pathCategory} />
      )
    }

    // Certain steps should show a typing indicator
    if (
      [AnimationStep.Opened, AnimationStep.MessageOneSent, AnimationStep.MessageTwoSent].includes(
        currentStep
      )
    ) {
      isTyping = true
    }

    return (
      <>
        {isTyping && <TypingIndicator names={expertName ? [expertName] : []} />}
        {messages.reverse().map((message, index) => {
          return <React.Fragment key={index}>{message}</React.Fragment>
        })}
      </>
    )
  }

  public renderExpandedChat(
    animatedStyles: React.HTMLAttributes<HTMLDivElement>['style'],
    content: ContentfulChatTeaserData
  ) {
    const { expert } = this.props

    if (!expert) {
      return null
    }

    return (
      <animated.div key="chat" style={animatedStyles} className={styles.chat}>
        <ChatHeader
          expert={expert}
          online={true}
          minimized={true}
          ctaIcon={CloseIcon}
          ctaText="Close chat"
          onCtaClick={this.collapse}
          onClick={this.collapse}
          className={styles.header}
        />
        <div className={styles.conversation}>
          {this.renderChatContents(content)}
          <ConversationStarter expert={expert} />
        </div>
      </animated.div>
    )
  }

  public renderCollapsedChat(
    animatedStyles: React.HTMLAttributes<HTMLDivElement>['style'],
    _content: ContentfulChatTeaserData
  ) {
    const { expert } = this.props

    if (!expert) {
      return null
    }

    return (
      <animated.div
        key="snackbar"
        style={animatedStyles}
        className={classnames(styles.snackbar, this.props.className)}
      >
        <ChatHeader
          expert={expert}
          online={true}
          minimized={false}
          ctaIcon={ChatIcon}
          ctaText="Chat"
          onClick={this.expand}
          onCtaClick={this.expand}
        />
      </animated.div>
    )
  }

  public render() {
    const { expert, pathCategory } = this.props

    if (!pathCategory || !pathCategory.slug) {
      return null
    }

    const widget = (
      <ContentfulQuery template="chatTeaser" variation={pathCategory.slug}>
        {({ content }: { content: ContentfulChatTeaserData }) => {
          if (!content) {
            return null
          }

          return (
            <div
              className={classnames(styles.widget, {
                [styles.expanded]: this.state.expanded
              })}
            >
              {expert ? (
                <Transition<boolean, {}, {}, {}, {}, {}>
                  native
                  items={this.state.expanded}
                  from={{ opacity: 0, transform: 'translate3d(0,100%,0)' }}
                  enter={{ opacity: 1, transform: 'translate3d(0,0%,0)' }}
                  leave={{ opacity: 0, transform: 'translate3d(0,100%,0)' }}
                >
                  {expanded => expanded
                    ? animatedStyles => this.renderExpandedChat(animatedStyles, content)
                    : animatedStyles => this.renderCollapsedChat(animatedStyles, content)}
                </Transition>
              ) : null}
            </div>
          )
        }}
      </ContentfulQuery>
    )

    // The TeaserChatWidget can optionally be rendered into a ReactPortal to allow for more control over positioning.
    const parentNode = this.props.getParent && this.props.getParent()
    const bodyNode = document.querySelector('body') as Element

    return ReactDOM.createPortal(widget, parentNode || bodyNode)
  }
}

export default withRouter(TeaserChatWidget)
